import { Session } from "next-auth";

import { isAdmin } from "@/lib/auth/rbac";
import { N8nRefLetterValidationError } from "@/lib/referees/n8nRefLetterPayload";
import { RefLetterGenerationOptions } from "@/lib/referees/refLetterOptions";

import { getApplicationById, getUserById } from "@/core/server";

import { buildN8nRefLetterPayload } from "./buildN8nRefLetterPayload";

const N8N_SUBMIT_WEBHOOK_URL = process.env.N8N_SUBMIT_WEBHOOK_URL;
const N8N_WEBHOOK_TIMEOUT_MS = 15_000;

export type GenerateAiRefLetterParams = {
  session: Session;
  applicationId: string;
  refereeId: string;
  generationOptions?: RefLetterGenerationOptions;
};

export type GenerateAiRefLetterResult = {
  status: "started";
  applicationId: string;
  refereeId: string;
};

export class GenerateAiRefLetterForbiddenError extends Error {
  constructor(message = "Forbidden") {
    super(message);
    this.name = "GenerateAiRefLetterForbiddenError";
  }
}

export class GenerateAiRefLetterNotConfiguredError extends Error {
  constructor(message = "N8N_SUBMIT_WEBHOOK_URL is not configured") {
    super(message);
    this.name = "GenerateAiRefLetterNotConfiguredError";
  }
}

export class GenerateAiRefLetterWebhookError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "GenerateAiRefLetterWebhookError";
    this.status = status;
  }
}

function formatN8nWebhookError(status: number, responseText: string): string {
  try {
    const parsed = JSON.parse(responseText) as {
      message?: string;
      hint?: string;
    };
    if (parsed.message && parsed.hint) {
      return `${parsed.message} ${parsed.hint}`;
    }
    if (parsed.message) {
      return parsed.message;
    }
  } catch {
    // fall through to raw text
  }

  if (responseText) {
    return `n8n webhook returned ${status}: ${responseText}`;
  }

  return `n8n webhook returned ${status}`;
}

export async function generateAiRefLetterForApplication({
  session,
  applicationId,
  refereeId,
  generationOptions,
}: GenerateAiRefLetterParams): Promise<GenerateAiRefLetterResult> {
  const ownUser = await getUserById(session.user.id);
  if (!ownUser || !isAdmin(ownUser)) {
    throw new GenerateAiRefLetterForbiddenError();
  }

  const application = await getApplicationById(applicationId);
  if (!application || application.visaType?.slug !== "gtv") {
    throw new GenerateAiRefLetterForbiddenError("GTV admin access required");
  }

  if (!N8N_SUBMIT_WEBHOOK_URL?.trim()) {
    throw new GenerateAiRefLetterNotConfiguredError();
  }

  const caseworkerEmail = ownUser.email?.trim();
  if (!caseworkerEmail) {
    throw new N8nRefLetterValidationError(["caseworkerEmail"]);
  }

  const payload = await buildN8nRefLetterPayload({
    applicationId,
    refereeId,
    caseworkerEmail,
    generationOptions,
  });

  console.log(
    `📤 Sending AI ref letter to n8n: applicationId=${applicationId}, refereeId=${refereeId}, selectClient=${
      payload.selectClient
    }, callbackUrl=${payload.platformCallbackUrl ?? "not set"}`
  );

  const controller = new AbortController();
  const timeoutId = setTimeout(
    () => controller.abort(),
    N8N_WEBHOOK_TIMEOUT_MS
  );

  try {
    const response = await fetch(N8N_SUBMIT_WEBHOOK_URL.trim(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    if (!response.ok) {
      const responseText = await response.text().catch(() => "");
      throw new GenerateAiRefLetterWebhookError(
        response.status,
        formatN8nWebhookError(response.status, responseText)
      );
    }
  } finally {
    clearTimeout(timeoutId);
  }

  console.log(
    `✅ AI ref letter request accepted by n8n: applicationId=${applicationId}, refereeId=${refereeId}`
  );

  return {
    status: "started",
    applicationId,
    refereeId,
  };
}
